/**
 * ## ItemsProvider.tsx ##
 * This file contains ItemsProvider component
 * @packageDocumentation
 */

import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Location } from 'history';

import once from 'lodash/once';
import { AnySchema, ValidationError } from 'yup';

import { BaseParams } from '@common/typescript/objects/BaseParams';
import {
	addPrefix,
	getKeysByPrefix,
} from '@common/react/utils/ObjectKeysPrefix/objectKeysPrefix';
import { generateGUID, clearListFields, returnAfterSubmit } from '@common/react/utils/utils';

import { List } from '@common/typescript/objects/List';
import { WithDeleted } from '@common/typescript/objects/WithDeleted';
import { handleUrl, getSearchParamsFromUrl } from '@common/react/utils/FIltersParamsFromUrl/FiltersParamsFromUrl';
import useRequest from '@common/react/hooks/useRequest';
import { useRequestProviderContext } from '@common/react/components/RequestProvider/RequestProvider';
import useAbortController from '@common/react/hooks/useAbortController';
import { useModal } from '@common/react/components/Modal/ModalContextProvider';
import {
	ClearForSubmitDelegate,
	TransformAfterSaveParams,
	TransformAfterSaveDelegate,
	HandleChangeEventElement,
	Mode,
} from '@common/react/objects/Core';

export type { List } from '@common/typescript/objects/List';
export type { HandleChangeEventElement } from '@common/react/objects/Core';
export { Mode } from '@common/react/objects/Core';

export enum SortingDirection {
	Default = 0,
	Ascending = 1,
	Descending = 2
}

/**
 * used to define sorting
 */
export interface ColumnFilter {
	caption: string;
	direction: SortingDirection;
}

export interface WithKey extends WithDeleted {
	keyGUID?: string;
}

export interface Edits<T extends WithKey> {
	[key: string]: T;
}

interface Loaders {
	[key: string]: boolean;
}

interface Errors {
	[key: string]: {
		err: BaseParams,
		submitCount: number;
	};
}

interface FiltersRef {
	filters: BaseParams;
	silent?: boolean;
	concatResult?: boolean;
	resetFilters?: boolean;
}

/**
 * defines pagination
 *
 * @interface PaginationState
 * @prop current Current page number
 * @prop pageSize Number of data items per page
 * @prop total Total number of data items
 */
export interface PaginationState {
	/**
	 * Current page number
	 */
	current: number;
	/**
	 * Number of data items per page
	 */
	pageSize: number;
	/**
	 * Total number of data items
	 */
	total: number;
}

export type PaginationOptions = Partial<PaginationState>

/**
 * type of second argument RenderItem
 *
 * @interface RenderItemProps
 * @template {WithKey} T - items type
 * @template TFilter - filter type
 * @template {List<any>} TResponse - request response type
 */
export interface RenderItemProps<
	T extends WithKey,
	TFilter extends BaseParams = BaseParams,
	TResponse extends List<any> = List<T>
> {
	/**
	 * item loading state
	 */
	loading: boolean;
	/**
	 * callback to save the passed element
	 * @param item - data that will replace the element and will be sent in the request
	 * @param saveRequest - custom request name, if you need a request different from the request in ItemsProvider
	 */
	save: (item?: T, saveRequest?: string) => void;
	/**
	 * update the current element without request
	 * @param item - partial data that will replace the value in the element
	 */
	update: (item: Partial<T>) => void;
	/**
	 * callback to update and save the current element
	 * @param item - partial data that will replace the value in the element before sending the request
	 * @param saveRequest - custom request name, if you need a request different from the request in ItemsProvider
	 */
	updateAndSave: (item: Partial<T>, saveRequest?: string) => void;
	/**
	 * callback to reload ItemsProvider data
	 * @param pagination - new pagination options
	 * @param resetFilters - determines whether filters need to be reset to default
	 */
	reload: LoadDelegate<T, TFilter & Omit<PaginationOptions, 'total'>, TResponse>;
	/**
	 * callback to add new items at ItemsProvider
	 * @param item - new item data
	 */
	add: (item?: Partial<T>, useIdFromItem?: boolean, isEdit?: boolean) => T;
}

/**
 * LoadOption - option for LoadDelegate
 *
 * @interface LoadOption
 * @template {WithKey} T - items type
 */
interface LoadOption<T extends WithKey> {
	/**
	 * If true load new items without change 'loading' state
	 *  for example if we want to update an element without showing a loading indicator
	 */
	silent?: boolean;
	/**
	 * Defines whether to replace old elements with new ones or add them to old ones.
	 *  used in loadMore function
	 */
	concatResult?: boolean;
	/**
	 * Determines whether filters should be reset to default or not
	 */
	resetFilters?: boolean;
	/**
	 * If true work as Array.reverse(items)
	 */
	reverseResult?: boolean;
	/**
	 * {use: boolean}. If you need prevent setState after load, change use to false
	 *  change as reference useResult.use = false
	 */
	useResult?: { use: boolean };
	/**
	 * edits after save
	 */
	edits?: Edits<T>;
}

/**
 * used for load, reload and loadDelay methods
 *
 * @template {WithDeleted} T - items type
 * @template TFilter - filter type
 * @template {List<any>} TResponse - request response type
 * @param params  - load filters, page number and number of items per page
 * @param options - load options {@link LoadOption}
 * @returns request promise
 */
export type LoadDelegate<T extends WithKey, TFilter extends BaseFilters, TResponse> = (
	/**
	 * load filters, page number and number of items per page
	 */
	params?: Partial<TFilter & BaseFilters> | BaseFilters,
	/**
	 * load options
	 */
	options?: LoadOption<T>,
) => Promise<TResponse>;

/**
 * used for handleChange method
 *
 * @template TFilter - filter type
 * @template {List<any>} TResponse - request response type
 * @param params        - filters or HandleChangeEventElement
 * @param sendRequest   - determines whether the request will be sent
 * @returns request promise
 */
export type HandleChangeDelegate<TFilter extends BaseFilters, TResponse> = (
	params?: Omit<Partial<TFilter>, 'currentTarget'> | HandleChangeEventElement,
	sendRequest?: boolean,
) => Promise<TResponse | undefined>;

/**
 * used to render the list element
 *
 * @template {WithDeleted} T - items type
 * @template TFilter - filter type
 * @param item  - The element for which the render is called
 * @param props - The element state and action parameters
 * @returns item markup
 */
export type RenderItem<T extends WithKey, TFilter extends BaseParams = BaseParams> =
	(item: T, props: RenderItemProps<T, TFilter>) => React.ReactNode;

type LikeList<T extends WithKey> = List<T> & { [key in string]: any }

type ListResponseRest<T extends WithKey, TResponse = List<T>> = Omit<TResponse, 'count' | 'execution' | 'list' | 'offset'>;

/**
 * ItemsProvider component props
 *
 * @interface ItemsProviderProps
 * @template {WithDeleted} T - items type
 * @template TFilter - filter type
 * @template {List<any>} TResponse - request response type
 */
export interface ItemsProviderProps<
	T extends WithKey,
	TFilter extends BaseParams = BaseParams,
	TResponse extends List<any> = List<T>
> {
	/**
	 * load request name. The default is made up of type.
	 * "List" is automatically added to the end. To prevent this from happening, use loadRequestExact
	 * @default null
	 */
	loadRequest: string | null;
	/**
	 * set true if you need loadRequest without 'List' at end
	 * @default false
	 */
	loadRequestExact?: boolean;
	/**
	 * - 1. ReactElement to be wrapped in an ItemsProvider context
	 * - 2. function with context ItemsProvider as first argument
	 */
	children?: React.ReactNode | ((context: ItemsProviderContext<T, TFilter, TResponse>) => React.ReactNode);
	/**
	 * default items
	 * @default []
	 */
	items?: Array<T>;
	/**
	 * default filters
	 */
	filters?: TFilter & BaseFilters;
	/**
	 * save request name. The default equal type.
	 * @default loadRequest without "List"
	 */
	saveRequest?: string;
	/**
	 * save all request name.
	 * @default ''
	 */
	saveAllRequest?: string;
	/**
	 * remove request name.
	 * @default saveRequest
	 */
	removeRequest?: string;
	/**
	 * remove all request name. filters object is used as request parameter
	 * two options:
	 *  - with ids list to remove selected items
	 *  - with removeAll = true, to remove all items
	 * @default ''
	 */
	removeAllRequest?: string;
	/**
	 * default pagination value
	 */
	pagination?: PaginationOptions;
	/**
	 * add - used when add from a context is called without arguments. Sets default values for a new element
	 * @param items - current items in ItemsProvider state. items can be useful when you need to put an index for a new element
	 * @returns new ItemsProvider element
	 */
	add?: (items: Array<T>) => T;
	/**
	 * defines whether it is allowed to edit several elements at the same time
	 * @default false
	 */
	multiple?: boolean;
	/**
	 * schema for checking elements before saving.
	 * @default undefined
	 */
	validationSchema?: AnySchema;
	/**
	 * transform item before save on server
	 * for more details and examples of using look at ItemProvider docs
	 * @default () => {} - doesn't clear any field, all fields will be sent to server
	 */
	clearForSubmit?: ClearForSubmitDelegate<T>;
	/**
	 * transform item after save
	 * for more details and examples of using look at ItemProvider docs
	 * @default ({ item }) => item - doesn't transform anything, since item contains id from server response,
	 * only id will be changed
	 */
	transformAfterSave?: TransformAfterSaveDelegate<T, T>;
	/**
	 * error handling function for all requests
	 * @param error - error text
	 */
	onRequestError?: ((error: string) => void);
	/**
	 * error handling function for load request and will be used instead of onRequestError
	 * @param error - error text
	 */
	onLoadRequestError?: ((error: string) => void);
	/**
	 * error handling function for save, saveAll and removeAll requests and will be used instead of onRequestError
	 * @param error - error text
	 */
	onSaveRequestError?: ((error: string) => void);
	/**
	 * error handling function for remove request and will be used instead of onRequestError
	 * @param error - error text
	 */
	onRemoveRequestError?: ((error: string) => void);
	/**
	 * Validation error handling function
	 */
	onValidationError?: ((key: string | number, err: any, error: Error) => void);
	mode?: Mode;
	/**
	 * key to store the functions of the child Item Provider
	 */
	objectKey?: string;
	/**
	 * object key to save results after calling a function from ref
	 */
	arrayName?: string;
	/**
	 * load callback
	 * @param res - request result
	 */
	onLoad?: (res: TResponse) => void;
	/**
	 * function to convert items after load
	 * @param items - array of items
	 * @param filters - current filter
	 */
	transformItems?: (items: Array<T>, filters: TFilter) => Array<T>;
	/**
	 * filters that will not be put in the url. These filters are expected to be fixed
	 */
	unhandledFilters?: TFilter & BaseFilters;
	/**
	 * A parameter that determines whether to add filters to the url
	 * @default false
	 */
	withHandleUrl?: boolean;
	/**
	 * filter processing before putting in url.
	 */
	transformFiltersBeforeHandleUrl?: (filters: TFilter) => BaseParams;
	/**
	 * url pathname
	 * @default ''
	 */
	path?: string;
	/**
	 * prefix for filters when withHandleUrl is true, it will add before each url filter key
	 * @default ''
	 */
	filtersPrefix?: string;
	/**
	 * how handle page in url.
	 * `pathname/${page}` or `${pathname}?page=${page}`
	 * @default false
	 */
	pageInSearch?: boolean;
	/**
	 * add new element as first or end
	 * @default false
	 */
	addedFirst?: boolean;
	/**
	 * will set that filters if load called by resetFilters flag
	 */
	defaultFilters?: TFilter & BaseFilters;
	/**
	 * callback used after load if resetFilters is true
	 */
	afterResetFilters?: (filters?: TFilter) => void; // for clear autocompletes or other filters
	/**
	 * default edit items
	 */
	editItems?: Edits<T>;
	/**
	 * that items will set in itemsProvider state
	 * used as dependencies in useEffect
	 */
	syncItems?: Array<T>;
	/**
	 * wait in ms before sending a request
	 * @default 0
	 */
	delay?: number;
	/**
	 * callback. Called when items changed inside ItemsProvider
	 */
	onItemsChange?: (items: Array<T>, filters: TFilter, res?: TResponse) => void;
	skipValidationAll?: boolean;
	/**
	 * render one item
	 */
	render?: RenderItem<T, TFilter>;
	/**
	 * callback. Called before save one item
	 */
	beforeSave?: (item: Array<T>, oldItem: Array<T>, callSave: () => any, loaders: (loading: boolean) => void) => any;
	/**
	 * callback. Called after save one item
	 */
	onSave?: (item: T, response?: T) => void;
	/**
	 * callback. Called after save all edit items. After receiving a response, reloads the data
	 */
	onSaveAll?: (items: Array<T>, response: any) => void;
	/**
	 * callback. Called after remove one item
	 */
	onRemoveItem?: (id: number) => void;
	/**
	 * init load option
	 *
	 * @default solved from items
	 */
	skipInitLoad?: boolean;
	/**
	 * get parameters from url
	 */
	searchParamsFromUrl?: (location: Location, prefix?: string) => TFilter;
	/**
	 * time to live (ms) for cached response at RequestProvider if cache is available
	 */
	ttl?: number;
	/**
	 * use when the response does not return a list. For example, we can convert an array to a list.
	 *
	 * - (res: Array<T>) => ({ list: res, offset: 0, execution: 0, offset: 0 })
	 *
	 * or we can solve some value and put it in otherData. fields from the returned object will be placed in
	 * otherData, excluding offset, execution, list, count. otherData type is Omit<LikeList<T>, 'list' | 'count' | 'execution' | 'offset'>
	 *
	 * - (res: Array<T>) => ({ list: res, count: res.length, execution: 0, offset: 0 })
	 */
	transformResponse?: (res: TResponse, filters: TFilter) => LikeList<T>;
	/**
	 * if the flag is true syncItems will be used as items directly without using useEffect
	 */
	useSyncItemsInsteadHook?: boolean;
	/**
	 * slice loaded list if count > pageSize.
	 * @default true
	 */
	sliceResultListByPageSize?: boolean;
	/**
	 * if true, then handleChange will send a request every time it is called.
	 *
	 * if false, only filters will be changed without sending a request
	 *
	 * used as default value for the second argument in handleChange
	 *
	 * @default true
	 */
	sendRequestAtHandleChange?: boolean;
	/**
	 * if true will show confirmation modal before removeRequest
	 * @default true
	 */
	removeConfirm?: boolean;
	/**
	 * transform data for remove request
	 * if it undefined, used default version that returns { id: item.id, deleted: true }
	 * @default undefined
	 * @param item         - item that will be removed
	 * @returns data for remove request
	 */
	transformItemBeforeRemove?: (item: T) => any;
	/**
	 * items Rows, as an alternative to pagination and items
	 */
	rows?: TResponse;
	/**
	 * paginationUrl
	 */
	paginationUrl?: {
		pageKey?: string;
		defaultPageSize?: number;
		defaultPageNumber?: number;
	},
}

/**
 * ItemsProvider context state
 *
 * @interface ItemsProviderContextState
 * @template {WithDeleted} T - items type
 * @template TFilter - filter type
 * @template {List<any>} TResponse - request response type
 */
export interface ItemsProviderContextState<
	T extends WithKey,
	TFilter extends BaseParams = BaseParams,
	TResponse extends List<any> = List<T>
> {
	/**
	 * items
	 */
	items: Array<T>;
	/**
	 * ItemsProvider loading state
	 */
	loading: boolean;
	/**
	 * ItemsProvider pagination
	 */
	pagination: PaginationState;
	/**
	 * filters
	 */
	filters: TFilter & BaseFilters;
	/**
	 * stores edit items, key is item id. if item is new id < 0
	 */
	edits: Edits<T>;
	/**
	 * stores items validation errors and submit count
	 */
	errors: Errors;
	loaders: Loaders;
	/**
	 * defines whether it is allowed to edit several elements at the same time
	 */
	multiple: boolean;
	/**
	 * load request name
	 */
	loadRequest: string | null;
	/**
	 * array of selected items
	 */
	selectedRows: Array<T>;
	/**
	 * OtherData is used when a load request returns something other than count, execution, offset and list.
	 */
	otherData: ListResponseRest<T, TResponse>;
	/**
	 * add new element as first or last
	 */
	addedFirst?: boolean;
	/**
	 * if wrapped by AdvancedItemsProvider
	 */
	advancedItems?: Array<T>;
	/**
	 * transform item after save
	 */
	transformAfterSave: (params: Omit<TransformAfterSaveParams<T, T>, 'returnAfterSubmit'>) => T;
	/**
	 * link to prevent reload from being called. For example,
	 * after deleting an element, if elements are updated from notifications
	 */
	deleting: React.MutableRefObject<boolean>;
	/**
	 * error message
	 */
	error: string;
	/**
	 * save all request name
	 */
	saveAllRequest?: string;
	/**
	 * remove all request name
	 */
	removeAllRequest?: string;
}

/**
 * ItemsProvider context actions
 *
 * @interface ItemsProviderContextActions
 * @template {WithDeleted} T - items type
 * @template TFilter - filter type
 * @template {List<any>} TResponse - request response type
 */
export interface ItemsProviderContextActions<
	T extends WithDeleted,
	TFilter extends BaseParams = BaseParams,
	TResponse extends List<any> = List<T>
> {
	/**
	 * load new items for ItemsProvider
	 */
	load: LoadDelegate<T, TFilter, TResponse>;
	/**
	 * save item
	 * @param record - data sent in the request
	 * @param skipValidation - ignore validation or no. By default is undefined
	 * @param saveRequest - requestName. By default used saveRequest from ItemsProvider props
	 * @returns promise from save request
	 */
	save: (record: Partial<Omit<T, 'id'>> & { id: number }, skipValidation?: boolean, saveRequest?: string) => Promise<T>;
	/**
	 * save all items
	 * @param skipValidation - ignore validation or not. By default, is undefined
	 * @param reloadHandler  - reload callback. By default, used 'reload' function
	 * @returns promise from save all request
	 */
	saveAll: (skipValidation?: boolean, reloadHandler?: LoadDelegate<T, TFilter, TResponse>) => Promise<void> | undefined;
	/**
	 * update items
	 * @param items - set that items in ItemsProvider state
	 * @param resetEdits - reset edits or not. default true
	 */
	updateItems: (items: Array<T>, resetEdits?: boolean) => void;
	/**
	 * add a new element to items and to edits if isEdit = true
	 * if useIdFromItem is not true, replace id of item with generated temporary negative id
	 * so for adding already saved item need to use with useIdFromItem = true or 'setItems' method instead
	 *
	 * @example add item ({ ...add(items), id: -1 }) to items and edits:
	 * addItem();
	 * @example add item ({ id: -1, name: 'Item Name' }) to items and edits:
	 * addItem({ name: 'Item Name' });
	 * @example add item ({ id: -1, name: 'Item Name' }) to items and edits:
	 * addItem({ id: 1, name: 'Item Name' });
	 * @example add item ({ id: 1, name: 'Item Name' }) to items and edits, important here we lost id at item we add:
	 * addItem({ id: 1, name: 'Item Name' }, true);
	 * @example add item ({ id: 1, name: 'Item Name' }) to items, important here we lost id at item we add:
	 * addItem({ id: 1, name: 'Item Name' }, true, false);
	 *
	 * @param item - set that item in ItemsProvider state. default value is determined from 'add' props
	 * @param useIdFromItem - use id from item or not. default false
	 * @param isEdit - add or not item to edits. default true
	 * @returns element that can be used for saving via 'save' method
	 */
	addItem: (item?: Partial<T>, useIdFromItem?: boolean, isEdit?: boolean) => T;
	/**
	 * function to change the state of edits in ItemsProvider. from the function you can set which elements are edited and what their values are.
	 */
	setEdits: React.Dispatch<React.SetStateAction<Edits<T>>>;
	setErrors: React.Dispatch<React.SetStateAction<Errors>>;
	/**
	 * reload items.
	 */
	reload: LoadDelegate<T, TFilter & Omit<PaginationOptions, 'total'>, TResponse>
	/**
	 * add new or update item in edits.
	 * edits dictionary usually using to store elements in edit mode, so by this function you can set which element is being
	 * edited and what its value is.
	 * @param record        - item to be edited
	 */
	setEdit: (record: T) => void;
	/**
	 * callback to update items
	 * @param items - new items value
	 * @param resetEdits - reset edits or not. default true
	 */
	setItems: (items: Array<T> | ((items: Array<T>) => Array<T>), resetEdits?: boolean) => void;
	/**
	* used to validate inner itemsProvider items
	* */
	addRef: (key: string | number, f: any) => void;
	/**
	 * callback to update items
	 * @param items - new items value
	 */
	setSelectedRows: React.Dispatch<React.SetStateAction<Array<T>>>;
	/**
	 * callback to remove passed elements
	 * @param items - values to be removed
	 * @param reloadHandler - used to prevent reload. By default, used 'reload' function
	 * @param confirmOptions - confirmation modal options
	 * @param removeAll - works only with 'removeAllRequest'
	 * @returns promise from remove all request or save all request
	 * if removeAll === true make 'removeAllRequest' request with { removeAll: true }
	 * else
	 *  if 'removeAllRequest' not empty make 'removeAllRequest' request with { ids: [] }
	 *  else make 'saveAllRequest' request with array of { id, deleted: true }
	 */
	removeItems: (
		items: Array<T>,
		reloadHandler?: LoadDelegate<T, TFilter, TResponse>,
		confirmOptions?: BaseParams | null,
		removeAll?: boolean,
	) => Promise<void>;
	/**
	 * behavior depends on second argument (default value is sendRequestAtHandleChange from props).
	 *
	 * if true: change filters and load new items
	 *
	 * if false: just change filters without sending a request
	 *
	 * Processes params before calling load if HandleChangeEventElement is passed
	 * This callback function handles filters returning HandleChangeEventElement (for example SimpleSearchInput or SelectFilter).
	 * If you use load as props for this filter without processing, an error will occur.
	 */
	handleChange: HandleChangeDelegate<TFilter & BaseFilters, TResponse>;
	/**
	 * load new items and concat them with previous items
	 * @param data          - load filters
	 * @param reverseResult - If true work as Array.reverse(items)
	 * @param silent        - If true load new items without change 'loading' state
	 * @returns promise from load request
	 */
	loadMore: (data?: Partial<TFilter> & BaseFilters, reverseResult?: boolean, silent?: boolean) => Promise<TResponse>;
	/**
	 * save all edit items
	 * @param items         - items to be sent in the request
	 * @param reloadHandler - used to prevent reload. By default, used 'reload' function
	 */
	saveItems: (items: Array<T>, reloadHandler?: LoadDelegate<T, TFilter, TResponse>) => void;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	validateAll: (skipValidation?: boolean) => Promise<Array<T> | undefined>;
	/**
	 update one item state
	 * @param item         - the new value of the item. result is {...prev, ...item};
	 */
	updateItem: (item: Omit<Partial<T>, 'id'> & WithDeleted) => void;
	/**
	 * remove item
	 * @param record         - item or item id
	 * @param confirmOptions - confirmation modal options
	 * @param requestName    - requestName. By default, used removeRequest from ItemsProvider props
	 * @returns promise from remove request
	 */
	remove: (record: number | T, confirmOptions?: BaseParams | null, requestName?: string) => Promise<T>;
	/**
	 * remove item without request by key and value
	 * @param value               - the value by which the item will be searched
	 * @param getValueFromItem    - function to get value from item. by default (item) => item.id
	 */
	removeItem: (value: any, getValueFromItem?: (item: T) => any) => void;
}

/**
 * BaseFilters
 *
 * @interface BaseFilters
 */
export interface BaseFilters {
	column?: Array<ColumnFilter>;
	text?: string;
	page?: number;
	pageSize?: number;
	offset?: number;
	count?: number;
}

/**
 * ItemsProvider context
 *
 * @interface ItemsProviderContext
 * @template {WithKey} T - items type
 * @template TFilter - filter type
 * @template {List<any>} TResponse - request response type
 * @template TActions - actions type
 */
export interface ItemsProviderContext<T extends WithKey,
	TFilter extends BaseParams = BaseParams,
	TResponse extends List<any> = List<T>,
	TActions extends ItemsProviderContextActions<T, TFilter, TResponse> = ItemsProviderContextActions<T, TFilter, TResponse>,
> {
	/*
	 * ItemsProvider context state
	 */
	state: ItemsProviderContextState<T, TFilter, TResponse>;
	/*
	 * ItemsProvider context actions
	 */
	actions: TActions;
}

export const createItemsProviderContext = once(
	<T extends WithKey,
		TFilters extends BaseParams = BaseParams,
		TResponse extends List<any> = List<T>,
		TActions extends ItemsProviderContextActions<T, TFilters, TResponse> = ItemsProviderContextActions<T, TFilters, TResponse>>() =>
		React.createContext({} as ItemsProviderContext<T, TFilters, TResponse, TActions>),
);

/**
 * get ItemsProviderContext
 *
 * @template {WithKey} T - items type
 * @template TFilter - filter type
 * @template {List<any>} TResponse - request response type
 * @template TActions - actions type
 * @param required - if true throw exception when context is empty
 * @returns ItemsProviderContext object
 */
export const useItemsProviderContext = <T extends WithKey,
	TFilter extends BaseParams = BaseParams,
	TResponse extends List<any> = List<T>,
	TActions extends ItemsProviderContextActions<T, TFilter, TResponse> = ItemsProviderContextActions<T, TFilter, TResponse>
>(required: boolean = true): ItemsProviderContext<T, TFilter, TResponse, TActions> => {
	const context = React.useContext<ItemsProviderContext<T, TFilter, TResponse, TActions>>(
		createItemsProviderContext<T, TFilter, TResponse, TActions>(),
	);

	if (required && !context?.actions) throw 'Need ItemsProvider context!';

	return context;
};

/**
 * addGUID - adds the keyGUID property to the elements of the array
 *
 * @template {WithKey} T - items type
 * @param items - array of items
 * @returns array of items with keyGUID prop
 */
const addGUID = <T extends WithKey, >(items: Array<T>): Array<T> => items.map((item) => ({ ...item, keyGUID: generateGUID() }));

/**
 * defaultTransformFiltersBeforeHandleUrl - transform filters
 *
 * @param filters - BaseParams
 * @returns filters without pagination props
 */
export const defaultTransformFiltersBeforeHandleUrl = (filters: any) => ({
	...filters,
	page: undefined,
	count: undefined,
	current: undefined,
	showSizeChanger: undefined,
	total: undefined,
	offset: undefined,
	pageSizeOptions: undefined,
	pageSize: filters.pageSize || filters.count,
});

/**
 * ItemsProvider component.
 *
 * @example ItemsProvider with ReactNode inside:
 * <ItemsProvider<T, TFilter> loadRequest="request">{React.ReactNode}</ItemsProvider>
 *
 * @example ItemsProvider with loadRequest and render function as children:
 * <ItemsProvider<T, TFilter> loadRequest="request">{(context) => React.ReactNode}</ItemsProvider>
 *
 * @example ItemsProvider with render callback
 * <ItemsProvider<T, TFilter> loadRequest="request" render={(item, props) => SingleItem} />
 *
 * @example ItemsProvider with CustomList type, if load request return not List<T> use transformResponse to convert it:
 * <ItemsProvider<T, TFilter, TCustomList<T>>
 *   loadRequest="request"
 *   transformResponse={transformResponse}
 * >
 *   {(context) => React.ReactNode}
 * </ItemsProvider>
 *
 * @template {WithKey} T - items type
 * @template TFilter - filter type
 * @template {List<any>} TResponse - request response type
 * @param props - {@link ItemsProviderProps}
 * @type {React.FC<ItemsProviderProps>}
 * @returns React.ReactElement
 */
export const ItemsProvider: <T extends WithKey, TFilter extends BaseParams = BaseParams, TResponse extends List<any> = List<T>>(
	p: ItemsProviderProps<T, TFilter, TResponse>
) => React.ReactElement<T> = <T extends WithKey, TFilter extends BaseParams = BaseParams, TResponse extends List<any> = List<T>>(
	props: ItemsProviderProps<T, TFilter, TResponse>,
) => {
	const {
		filters = {} as TFilter,
		loadRequest: loadRequestProp,
		saveAllRequest = '',
		loadRequestExact,
		removeAllRequest = '',
		removeConfirm = true,
		children = null as React.ReactNode,
		add = () => ({ id: -1 } as T),
		multiple = false,
		validationSchema = undefined,
		clearForSubmit = () => {},
		transformAfterSave = ({ item }) => item,
		onLoadRequestError,
		onSaveRequestError,
		onRemoveRequestError,
		onValidationError = () => undefined,
		mode = Mode.View,
		objectKey = '',
		arrayName = '',
		onLoad,
		transformItems = (items) => items,
		unhandledFilters = {} as TFilter,
		transformFiltersBeforeHandleUrl = defaultTransformFiltersBeforeHandleUrl,
		withHandleUrl = false,
		filtersPrefix = '',
		path = '',
		pageInSearch = false,
		defaultFilters = {} as TFilter,
		afterResetFilters,
		addedFirst = false,
		editItems = undefined,
		syncItems = undefined,
		delay = 0,
		onItemsChange = undefined,
		skipValidationAll = false,
		render = undefined,
		onSave,
		beforeSave,
		onSaveAll,
		onRemoveItem,
		searchParamsFromUrl = getSearchParamsFromUrl as any,
		ttl,
		transformResponse = (res) => res,
		useSyncItemsInsteadHook,
		sliceResultListByPageSize = true,
		sendRequestAtHandleChange = true,
		transformItemBeforeRemove,
		rows,
		paginationUrl = {},
		...rest
	} = props;
	const loadRequest = loadRequestExact || !loadRequestProp || loadRequestProp.includes('List')
		? loadRequestProp
		: `${loadRequestProp}List`;
	const { openErrorMessage, openConfirm } = useModal();
	const {
		pageKey = 'page',
		defaultPageSize = 10,
		defaultPageNumber = 1,
	} = paginationUrl;
	const {
		list,
		count,
		...initOther
	} = rows || {};
	const {
		items = rows?.list,
		pagination = {
			current: defaultPageNumber,
			pageSize: defaultPageSize,
			total: rows?.count ?? 0,
		},
		saveRequest = loadRequest?.replace('List', ''),
		removeRequest = saveRequest,
		onRequestError = (err) => openErrorMessage?.(err),
		skipInitLoad = loadRequest ? !!(items || syncItems) : true,
	} = rest;
	const ItemsContext = createItemsProviderContext<T, TFilter, TResponse>();
	const location = useLocation();
	const navigate = useNavigate();

	const params = useParams<BaseParams>();
	const [selectedRows, setSelectedRows] = React.useState<Array<T>>([]);

	const [edits, _setEdits] = React.useState<Edits<T>>(editItems || {});
	const [innerItems, _setItems] = React.useState<Array<T>>(items ? addGUID(transformItems(items, filters)) as Array<T> : []);
	const _items = useSyncItemsInsteadHook ? syncItems || [] : innerItems;
	const [_otherData, setOtherData] = React.useState<ListResponseRest<T, TResponse>>(initOther as ListResponseRest<T, TResponse>);
	const [loaders, setLoaders] = React.useState<Loaders>({});
	const [loading, setLoading] = React.useState(false);
	const [error, _setError] = React.useState('');
	const [_filters, setFilters] = React.useState<TFilter>({
		...filters,
		pageSize: pagination.pageSize,
		current: pagination.current,
		...(withHandleUrl ? getKeysByPrefix(searchParamsFromUrl(location, filtersPrefix), filtersPrefix) : {}),
		...(withHandleUrl && !pageInSearch && params[pageKey] ? { current: +params[pageKey] } : {}),
	});
	const lastRequest = React.useRef<Promise<TResponse> | null>(null);
	const [_pagination, setPagination] = React.useState<PaginationState>({
		...(withHandleUrl
			? {
				...pagination,
				current: (pageInSearch
					? _filters.page || pagination.current
					: withHandleUrl && params[pageKey] ? +params[pageKey] : pagination.current) ?? defaultPageNumber,
				pageSize: _filters.pageSize || _filters.count || pagination.pageSize || defaultPageSize,
			}
			: {
				...pagination,
				current: pagination.current ?? defaultPageNumber,
				pageSize: pagination.pageSize ?? defaultPageSize,
			}),
		total: pagination.total ?? rows?.count ?? 0,
	});
	const [errors, setErrors] = React.useState<Errors>({});
	const [id, setId] = React.useState<number>(() => {
		if (items) {
			const minId = Math.min(...items.map((item) => item.id)) - 1;
			if (minId < 0) return minId;
		}
		return -1;
	});

	const filtersRef = React.useRef<FiltersRef | null>(null);
	const request = useRequest();
	const requestContext = useRequestProviderContext();
	const deleting = React.useRef<boolean>(false);
	const [controller] = useAbortController();

	const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

	const _clearForSubmit = (item: T) => ({ ...item, ...clearForSubmit({ item, clearListFields }) });

	const clearError = (items: Edits<T>, increment?: number) =>
		setErrors(Object.keys(items)
			.reduce((acc, key) => ({
				...acc,
				[key]: { err: undefined, submitCount: (errors[key]?.submitCount || 0) + (increment || 0) },
			}), {}));

	const setError = (key: string | number, err: any, increment?: number) =>
		setErrors((prev) => ({
			...prev,
			[key]: { err, submitCount: (prev[key]?.submitCount || 0) + (increment || 0) },
		}));

	const setEdits = (value: React.SetStateAction<Edits<T>>) => {
		_setEdits((prev) => {
			const newEdits = typeof value === 'function' ? value(prev) : value;

			clearError(newEdits);

			return newEdits;
		});
	};

	const refs = React.useRef<any>({});
	const setRef = (obj: any) => {
		refs.current = { ...refs.current, ...obj };
	};

	const initEdits = (items: Array<T>, edits: Edits<T> = {}): Edits<T> => {
		let _edits: Edits<T> = {};

		if (mode === Mode.Edit) {
			const temp: Edits<T> = {};
			let unsavedItemsCount = 0;

			for (let i = 0; i < items.length; i++) {
				const edit = edits[items[i].id];

				if (!edit) {
					if (items[i].id < 0) {
						items[i].id = -1 - unsavedItemsCount;
						unsavedItemsCount++;
					}
					temp[items[i].id] = items[i];
				} else {
					temp[items[i].id] = { ...edit, ...items[i] };
				}
			}
			setId((prev) => {
				const minId = Math.min(...items.map((item) => item.id));
				if (minId <= prev) return minId - 1;
				return prev;
			});

			_edits = { ...temp };
		}

		return _edits;
	};

	React.useEffect(() => {
		if (syncItems && !useSyncItemsInsteadHook) {
			_setItems(syncItems);
			setEdits(initEdits(syncItems, edits));
		}
	}, [syncItems]); // syncItems should be memoized or kept in state to avoid endless re-renders

	React.useEffect(() => {
		setEdits(initEdits(!_items.length ? items || [] : _items, editItems || edits));
	}, [mode, Object.keys(editItems || {}).sort().toString()]);

	React.useEffect(() => {
		if (withHandleUrl) {
			const transformedFilters = {
				...transformFiltersBeforeHandleUrl(_filters as TFilter),
				page: pageInSearch ? _filters.page || _filters.current : undefined,
			};
			const filters = filtersPrefix ? addPrefix(transformedFilters, filtersPrefix) : transformedFilters;

			handleUrl(filters, location, navigate, _filters.current, filtersPrefix, pageInSearch);
		}
	}, [_filters]);

	React.useEffect(() => {
		if (withHandleUrl && path && !params[pageKey] && location.search) {
			navigate({
				...location,
				pathname: `/${path.match(/\/d+$/) ? path : `${path}/${_filters.current || ''}`}`,
			}, { replace: true, state: location.state });
		}
	}, [_filters.current, location.search]);

	React.useEffect(() => {
		!_items.length && items?.length && _setItems(items);
	}, []);

	React.useEffect(() => {
		!skipInitLoad && reload({ page: _pagination.current || 1, pageSize: _pagination.pageSize || 10 })
			.catch((err) => (typeof err !== 'string' || !err?.includes('aborted')) && console.log(err));
		return () => {
			controller.abort();
		};
	}, []);

	React.useEffect(() => {
		if (skipInitLoad && items && requestContext?.actions?.updateCache) {
			const pageSize = _pagination.pageSize;
			const offset = _filters.offset || (pageSize !== _pagination.pageSize ? ((_filters.current || 1) - 1) * +pageSize : 0);
			requestContext.actions.updateCache(
				loadRequest,
				{
					...unhandledFilters,
					..._filters,
					showSizeChanger: undefined,
					offset,
					page: _filters.offset ? undefined : _filters.current,
					current: undefined,
					count: pageSize,
				},
				{
					...rows,
					list: items,
					count: _pagination?.total,
				},
				ttl,
			);
		}
	}, []);

	const setItems = (items: React.SetStateAction<Array<T>>, resetEdits: boolean = true) => {
		_setItems((prev) => {
			const newItems = typeof items === 'function' ? items(prev) : items;
			onItemsChange && onItemsChange(newItems, _filters as TFilter);

			resetEdits && setEdits((prev) => prev || initEdits(newItems, prev));
			return newItems;
		});
	};

	const updateItem = (item: Omit<Partial<T>, 'id'> & WithDeleted) => {
		setItems((prev) => prev.map((el) => (el.id === item.id ? { ...el, ...item, id: item.id } : el)));
	};

	const resetItems = (items: Array<T>, filters: TFilter, res?: TResponse, edits?: Edits<T>) => {
		let transformedItems = transformItems(items, filters);
		if (multiple) {
			transformedItems = addedFirst
				? Object.values(edits || {})
					.filter((item) => item.id < 0)
					.sort((a, b) => a.id - b.id)
					.concat(transformedItems)
				: transformedItems
					.concat(
						Object.values(edits || {})
							.filter((item) => item.id < 0)
							.sort((a, b) => b.id - a.id),
					);
		}

		_setItems(transformedItems);
		onItemsChange && onItemsChange(transformedItems, filters, res);
		setEdits(edits || initEdits(transformedItems, {}));
	};

	// "silent" used for load without showing loading

	const load: LoadDelegate<T, TFilter, TResponse> = (
		data: BaseFilters = { page: 1, pageSize: 10 },
		{
			resetFilters,
			reverseResult,
			concatResult,
			useResult = { use: true },
			silent,
			edits,
		} = { useResult: { use: true } } as LoadOption<T>,
	) => {
		const newPageSize = +(data.pageSize || _pagination.pageSize || 10);

		let newFilters: BaseParams = { ..._filters };

		resetFilters && Object.keys(_filters).forEach((key) => newFilters[key] = undefined);
		newFilters = {
			...newFilters,
			...(resetFilters ? { ...unhandledFilters, ...defaultFilters } : {}),
			...data,
			current: data.page || 1,
			showSizeChanger: undefined,
			count: _filters.count ? newPageSize : undefined,
		};

		const offset = concatResult
			? _items?.length
			: data.offset || (newPageSize !== _pagination.pageSize ? (newFilters.current - 1) * +newPageSize : 0);

		setFilters(newFilters as TFilter);
		resetFilters && afterResetFilters && afterResetFilters();
		const defaultList = {
			execution: 0,
			count: pagination.total,
			list: _items,
			offset: _filters.offset || (_pagination.pageSize * _pagination.current) - _items.length,
		} as TResponse;

		if (!loadRequest) {
			return Promise.resolve(defaultList);
		}

		const promise = request<TResponse>(loadRequest, {
			...unhandledFilters,
			...newFilters,
			offset,
			page: data.offset ? undefined : newFilters.current,
			current: undefined,
			pageSize: undefined,
			count: newPageSize,
		}, {
			beforeRequest: () => !silent && setLoading(true),
			ttl,
			signal: controller.signal,
		}).then((res) => {
			if (lastRequest.current === promise && useResult.use) {
				!silent && setLoading(false);
				const {
					count, execution, offset, list = [], ...otherData
				} = transformResponse(res, { ...unhandledFilters, ...newFilters });
				const resultItems = concatResult
					? reverseResult ? list.reverse().concat(_items) : _items.concat(list)
					: reverseResult
						? sliceResultListByPageSize ? list.slice(0, newPageSize).reverse() : list.reverse()
						: sliceResultListByPageSize ? list?.slice(0, newPageSize) : list;

				setOtherData(otherData as ListResponseRest<T, TResponse>);
				resetItems(addGUID(resultItems), newFilters as TFilter, res, mode === Mode.Edit ? initEdits(resultItems, edits) : edits);
				_setError('');

				setPagination((prev) => ({
					...prev,
					current: newFilters.current,
					pageSize: newPageSize,
					total: count,
				}));
				setSelectedRows(silent ? selectedRows.filter((item: T) => list.find(({ id }) => item.id === id)) : []);

				onLoad && onLoad(res);
				return res;
			}
			if (lastRequest.current === promise && !useResult.use) {
				!silent && setLoading(false);
			}

			return {
				execution: 0,
				count: pagination.total,
				list: _items,
				offset: _filters.offset || (_pagination.pageSize * _pagination.current) - _items.length,
			} as TResponse;
		}).catch((error) => {
			if (typeof error === 'string' && error.includes('aborted')) {
				throw error;
			}

			!silent && setLoading(false);
			_setError(error);

			onLoadRequestError ? onLoadRequestError(error) : onRequestError(error);

			throw error;
		});
		lastRequest.current = promise;
		return promise;
	};

	const loadDelay: LoadDelegate<T, TFilter, TResponse> = (
		data = { page: 1, pageSize: 10 },
		options,
	): Promise<TResponse> => {
		if (!delay) {
			return load(data, options);
		}

		filtersRef.current = {
			filters: { ...filtersRef.current?.filters, ...data },
			silent: options?.silent,
			concatResult: options?.concatResult,
			resetFilters: options?.resetFilters,
		};

		setFilters((prev) => ({
			...prev,
			...filtersRef.current?.filters,
			showSizeChanger: undefined,
			count: prev.count,
			pageSize: prev.pageSize,
		}));

		return new Promise(() => {
			if (timeoutRef.current !== null) {
				clearTimeout(timeoutRef.current as NodeJS.Timeout);
			}

			timeoutRef.current = setTimeout(() => {
				if (filtersRef.current !== null) {
					const {
						filters, silent, concatResult, resetFilters,
					} = filtersRef.current;
					filtersRef.current = null;
					return load(
						filters,
						{
							...options, silent, concatResult, resetFilters,
						},
					);
				}
				return {
					execution: 0,
					count: _pagination.total,
					list: _items,
					offset: _filters.offset || (_pagination.pageSize * _pagination.current) - _items.length,
				} as TResponse;
			}, delay);
		});
	};

	const loadMore = (data?: Partial<TFilter> & BaseFilters, reverseResult?: boolean, silent?: boolean) =>
		loadDelay(data, {
			silent, concatResult: true, resetFilters: false, reverseResult,
		});

	const handleChange: HandleChangeDelegate<TFilter, TResponse> = (params, sendRequest = sendRequestAtHandleChange) => {
		const withCurrentTarget = params && 'currentTarget' in params;
		const withName = withCurrentTarget && params?.currentTarget && 'name' in params.currentTarget;
		if (!sendRequest) {
			const { name, value } = withCurrentTarget && withName ? params?.currentTarget ?? {} : {};
			setFilters((prev) => ({
				...prev,
				...(withCurrentTarget && name ? { [name]: value } : params),
				showSizeChanger: undefined,
				count: prev.count,
				pageSize: prev.pageSize,
			}));
			return Promise.resolve({
				execution: 0,
				count: _pagination.total,
				list: _items,
				offset: _filters.offset || (_pagination.pageSize * _pagination.current) - _items.length,
			} as TResponse);
		}

		if (withCurrentTarget) {
			const { name, value } = params.currentTarget ?? {};
			return loadDelay({
				..._filters, offset: 0, page: 1, ...(name ? { [name]: value } : {}),
			});
		}
		return loadDelay({
			..._filters, ...params, offset: params?.offset || 0, page: params?.page ?? 1,
		});
	};

	const saveItem = (record: T, requestName: string | undefined = saveRequest) => {
		if (!requestName) {
			throw 'Need saveRequest';
		}

		const item = _clearForSubmit(record);

		if (refs.current?.[item.id]) {
			/*
			const r = refs[item.id](item);

			item = r.item;

			promises.push(r.validate);
			*/
		}

		setLoaders({ ...loaders, [record.id]: true });

		return request<T>(requestName, item).then((response) => {
			setLoaders({ ...loaders, [record.id]: undefined });

			onSave && onSave(item, response);

			return response;
		}).catch((error: string) => {
			setLoaders({ ...loaders, [record.id]: undefined });

			onSaveRequestError ? onSaveRequestError(error) : onRequestError(error);

			throw error;
		}).finally(() => setPagination((prev) => ({
			...prev,
			..._pagination,
		})));
	};

	const save = (record: Partial<Omit<T, 'id'>> & { id: number }, skipValidation?: boolean, saveRequest?: string) => {
		const modifiedRecord = { ...record, ...(record.deleted ? record : edits[record.id]) } as T;

		const _saveItem = (record: T, requestName?: string) => {
			const _editedItem = _items.find((q) => q.id === record.id);
			return beforeSave
				? beforeSave([modifiedRecord], _editedItem ? [_editedItem] : [], () => saveItem(record, requestName), setLoading)
				: saveItem(record, requestName);
		};
		if (!record.deleted && validationSchema && !skipValidation) {
			return validationSchema?.validate(modifiedRecord, { abortEarly: false }).then(() => {
				if (refs.current[modifiedRecord.id]) {
					const r = refs.current[modifiedRecord.id](modifiedRecord);

					return r.validate()
						.then((results: any) => {
							if (typeof results !== 'undefined') {
								return _saveItem(r.item, saveRequest);
							}
						});
				}
				return _saveItem(modifiedRecord, saveRequest);
			}).catch((err) => {
				if (err.inner) {
					const er = {};
					for (let i = 0; i < err.inner.length; i++) {
						er[err.inner[i].path] = err.inner[i].errors[0];
					}

					setError(record.id, er, 1);

					onValidationError(record.id, er, err.inner);

					if (refs.current[modifiedRecord.id]) {
						const r = refs.current[modifiedRecord.id](modifiedRecord);
						return r.validate();
					}
				} else {
					throw err;
				}
			}) as Promise<T>;
		}

		return _saveItem(modifiedRecord, saveRequest);
	};

	const reload: LoadDelegate<T, TFilter & Omit<PaginationOptions, 'total'>, TResponse> = (params, options) => {
		const page = (params && 'current' in params ? params?.current : undefined) || params?.page || _pagination.current;
		const pageSize = params?.pageSize || _pagination.pageSize;

		return loadDelay({
			...params,
			page: options?.resetFilters ? 1 : page,
			pageSize,
		}, options);
	};

	const updateItems = (items: Array<T>, resetEdits = true) => {
		setItems(items, resetEdits);
	};

	const saveItems = (items: Array<T>, reloadHandler: LoadDelegate<T, TFilter, TResponse> = reload) => {
		if (saveAllRequest === '') throw 'save all request is not available';

		setLoading(true);

		return request<TResponse>(saveAllRequest, items.map((value) => _clearForSubmit(value)))
			.then((response) => {
				setEdits(mode === Mode.Edit ? initEdits(items, edits) : {});

				onSaveAll && onSaveAll(items, response);
				reloadHandler(_pagination)
					.catch(onSaveRequestError || onRequestError)
					.finally(() => {
						setLoading(false);
					});
			})
			.catch((error: string) => {
				setLoading(false);
				onSaveRequestError ? onSaveRequestError(error) : onRequestError(error);
				throw error;
			});
	};

	const _remove = (requestName: string, id: number): Promise<T> => {
		setLoaders({ ...loaders, [id]: true });
		const item = transformItemBeforeRemove
			? { ...transformItemBeforeRemove(_items.find((item) => item.id === id)), deleted: true }
			: { id, deleted: true };

		return request<T>(requestName, item).then((response) => {
			setLoaders({ ...loaders, [id]: undefined });

			onRemoveItem && onRemoveItem(id);

			return response;
		}).catch((error: string) => {
			setLoaders({ ...loaders, [id]: undefined });

			onRemoveRequestError ? onRemoveRequestError(error) : onRequestError(error);

			throw error;
		}).finally(() => setPagination((prev) => ({
			...prev,
			..._pagination,
		})));
	};

	const _removeItems = (items: Array<T>, reloadHandler: LoadDelegate<T, TFilter, TResponse> = reload, removeAll?: boolean) => {
		if (removeAllRequest) {
			setLoading(true);

			return request<List<T>>(removeAllRequest, {
				...unhandledFilters,
				..._filters,
				...(removeAll ? { removeAll } : { ids: items.map((value) => value.id) }),
			})
				.then(() => {
					setEdits({});

					reloadHandler({})
						.catch(onSaveRequestError || onRequestError)
						.finally(() => {
							setLoading(false);
						});
				})
				.catch((error: string) => {
					setLoading(false);
					onRemoveRequestError ? onRemoveRequestError(error) : onRequestError(error);
					throw error;
				});
		}

		if (removeAll) {
			throw 'Need removeAllRequest to have ability remove all items';
		}

		return saveItems(items.map((value) => ({ id: value.id, deleted: true } as T)), reloadHandler);
	};

	const _removeConfirmation = (confirmOptions: any, handler: () => Promise<any>) => {
		if (removeConfirm && confirmOptions) {
			return new Promise((resolve, reject) => {
				openConfirm({
					okText: 'Yes',
					okType: 'danger',
					cancelText: 'Cancel',
					className: 'editable-table-delete-modal',
					...confirmOptions,
					onOk: () => {
						handler()
							.then((res) => resolve(res))
							.catch((err) => reject(err));
					},
				});
			}) as Promise<void>;
		}

		return handler();
	};

	const remove = (
		record: number | T,
		confirmOptions: BaseParams | null = {
			title: 'This item will be permanently deleted and cannot be recovered. Are you sure?',
		},
		requestName = removeRequest,
	): Promise<T> => {
		if (!requestName) {
			throw 'Need removeRequest';
		}

		const id = typeof record === 'number' ? record : record.id;

		return _removeConfirmation({
			okButtonProps: {
				'data-confirm': `${id}`,
			},
			...confirmOptions,
		}, () => _remove(requestName, id));
	};

	const removeItems = (
		items: Array<T>,
		reloadHandler: LoadDelegate<T, TFilter, TResponse> = reload,
		confirmOptions: BaseParams | null = {
			title: 'This items will be permanently deleted and cannot be recovered. Are you sure?',
		},
		removeAll?: boolean,
	) => _removeConfirmation({
		okButtonProps: {
			'data-confirm': 'all',
		},
		...confirmOptions,
	}, () => _removeItems(items, reloadHandler, removeAll));

	const validateAll = (skipValidation?: boolean, addSubmitCount?: boolean) => {
		const items: Array<T> = [];

		if (validationSchema && !skipValidation) {
			clearError(edits);

			const promises: Array<Promise<ValidationError | undefined>> = [];

			for (const key in edits) {
				const item = edits[key];

				items.push(_clearForSubmit(item));

				promises.push(validationSchema?.validate(item, { abortEarly: false })
					.catch((err) => {
						if (err.inner) {
							const er = {};
							for (let i = 0; i < err.inner.length; i++) {
								er[err.inner[i].path] = err.inner[i].errors[0];
							}

							setError(key, er, addSubmitCount ? 1 : 0);

							onValidationError(key, er, err);
						} else {
							throw err;
						}
					}));
			}

			if (promises.length > 0) {
				return Promise.all(promises).then((results) => {
					if (!results.some((q) => typeof q === 'undefined')) {
						return Promise.resolve(items);
					}

					// console.log(errors);
				});
			}
		} else {
			for (const key in edits) {
				items.push(_clearForSubmit(edits[key]));
			}
		}

		return Promise.resolve(items);
	};

	const saveAll = (skipValidation?: boolean, reloadHandler: LoadDelegate<T, TFilter, TResponse> = reload) => {
		if (saveAllRequest === '') throw 'save all request is not available';

		if (Object.keys(edits).length <= 0) throw 'There are no edited items';

		const _saveItems = (items: Array<T>) => {
			const _editItems = Object.keys(edits).map((key) => {
				return edits[key];
			});

			return beforeSave
				? beforeSave(_editItems, _items, () => saveItems(items, reloadHandler), setLoading)
				: saveItems(items, reloadHandler);
		};
		const items: Array<T> = [];

		if (validationSchema && !skipValidation) {
			clearError(edits, 1);

			const promises: Array<Promise<ValidationError | undefined>> = [];

			for (const key in edits) {
				let item = edits[key];

				if (refs[item.id]) {
					const r = refs[item.id](item);

					item = r.item;

					promises.push(r.validate());
				}

				items.push(_clearForSubmit(item));

				promises.push(validationSchema?.validate(item, { abortEarly: false })
					.catch((err) => {
						if (err.inner) {
							const er = {};
							for (let i = 0; i < err.inner.length; i++) {
								er[err.inner[i].path] = err.inner[i].errors[0];
							}

							setError(key, er);

							onValidationError(key, er, err);
						} else {
							throw err;
						}
					}));
			}

			if (promises.length > 0) {
				Promise.all(promises).then((results) => {
					if (!results.some((q) => typeof q === 'undefined')) {
						return _saveItems(items.sort((a, b) => (addedFirst ? 1 : -1) * (a.id - b.id)));
					}
				});
			}
		} else {
			for (const key in edits) {
				items.push(_clearForSubmit(edits[key]));
			}

			return _saveItems(items.sort((a, b) => (addedFirst ? 1 : -1) * (a.id - b.id)));
		}
	};

	const setEdit = (record: T) => {
		setEdits({ ...edits, [record.id]: record });
	};

	const addItem = (item?: Partial<T>, useIdFromItem = false, isEdit = true): T => {
		setId((prev) => prev - 1);
		const newId = (useIdFromItem ? item?.id : id) ?? id;

		const newItem = item ? { ...item, id: newId } as T : { ...add(_items), id: newId };

		if (!(useIdFromItem && _items.some((item) => item.id === newId))) {
			updateItems(addedFirst ? [newItem].concat(_items) : _items.concat(newItem));
		}

		if (isEdit) setEdit(newItem);
		return newItem;
	};

	const removeItem = (value: number | string, getValueFromItem = (item: T) => item?.id) => {
		setItems((prev) => prev.filter((item) => value !== getValueFromItem(item)));
	};

	const addRef = (key: string | number, f: any) => {
		setRef({ [key]: f });
	};

	const value: ItemsProviderContext<T, TFilter, TResponse> = {
		state: {
			items: _items,
			loading,
			pagination: _pagination,
			filters: _filters,
			edits,
			errors,
			loaders,
			multiple,
			loadRequest,
			addedFirst,
			otherData: _otherData,
			transformAfterSave: (params) => transformAfterSave({
				...params,
				returnAfterSubmit,
				item: params.item && { ...params.item, id: params.response.id },
			}),
			deleting,
			removeAllRequest,
			error,
			saveAllRequest,
			selectedRows,
		},
		actions: {
			load: loadDelay,
			save,
			saveAll,
			updateItems,
			addItem,
			setEdits,
			setErrors,
			reload,
			setEdit,
			setItems,
			addRef,
			removeItems,
			handleChange,
			loadMore,
			saveItems,
			setLoading,
			validateAll,
			updateItem,
			remove,
			removeItem,
			setSelectedRows,
		},
	};

	const context = useItemsProviderContext<WithKey>(false);

	if (context.state) {
		// ItemsProvider in ItemsProvider

		React.useEffect(() => {
			objectKey && arrayName && context.actions.addRef(objectKey, (item: T) => {
				const items: Array<T> = [];

				for (const key in edits) {
					items.push(_clearForSubmit(edits[key]));
				}

				return { item: { ...item, [arrayName]: items }, validate: () => validateAll(false, true) };
			});
		}, [edits, context?.state?.edits]);
	} else {
		React.useEffect(() => {
			!skipValidationAll && validateAll(false);
		}, [edits]);
	}

	return (
		<ItemsContext.Provider value={value}>
			{render && <React.Fragment key="renderItems">
				{_items.map((item) => render(item, {
					loading,
					reload,
					add: addItem,
					update: (newItem) => updateItem({ ...newItem, id: (item.id || newItem.id) as number }),
					save: (value, saveRequest) => save(value!, false, saveRequest),
					updateAndSave: (value, saveRequest) => save({ ...item, ...value, id: item.id }, false, saveRequest),
				}))}
			</React.Fragment>}
			{typeof children === 'function' ? children(value) : children}
		</ItemsContext.Provider>
	);
};
